import {
  CrewIncentiveTypeEnum,
  Pegasus,
  shippingClaimReasonName,
  shippingClaimResolutionMethodName,
  shippingClaimStatusName,
} from 'corso-types';

export {
  shippingClaimReasonName,
  shippingClaimResolutionMethodName,
  shippingClaimStatusName,
};

export const estimatedDeliveryShippingCutOffOptions = [
  { value: 0, label: 'None' },
  { value: 10, label: '10:00AM' },
  { value: 10.15, label: '10:15AM' },
  { value: 10.3, label: '10:30AM' },
  { value: 10.45, label: '10:45AM' },
  { value: 11, label: '11:00AM' },
  { value: 11.15, label: '11:15AM' },
  { value: 11.3, label: '11:30AM' },
  { value: 11.45, label: '11:45AM' },
  { value: 12, label: '12:00PM' },
  { value: 12.15, label: '12:15PM' },
  { value: 12.3, label: '12:30PM' },
  { value: 12.45, label: '12:45PM' },
  { value: 13, label: '1:00PM' },
  { value: 13.15, label: '1:15PM' },
  { value: 13.3, label: '1:30PM' },
  { value: 13.45, label: '1:45PM' },
  { value: 14, label: '2:00PM' },
  { value: 14.15, label: '2:15PM' },
  { value: 14.3, label: '2:30PM' },
  { value: 14.45, label: '2:45PM' },
  { value: 15, label: '3:00PM' },
  { value: 15.15, label: '3:15PM' },
  { value: 15.3, label: '3:30PM' },
  { value: 15.45, label: '3:45PM' },
  { value: 16, label: '4:00PM' },
  { value: 16.15, label: '4:15PM' },
  { value: 16.3, label: '4:30PM' },
  { value: 16.45, label: '4:45PM' },
  { value: 17, label: '5:00PM' },
  { value: 17.15, label: '5:15PM' },
  { value: 17.3, label: '5:30PM' },
  { value: 17.45, label: '5:45PM' },
  { value: 18, label: '6:00PM' },
  { value: 18.15, label: '6:15PM' },
  { value: 18.3, label: '6:30PM' },
  { value: 18.45, label: '6:45PM' },
  { value: 19, label: '7:00PM' },
  { value: 19.15, label: '7:15PM' },
  { value: 19.3, label: '7:30PM' },
  { value: 19.45, label: '7:45PM' },
  { value: 20, label: '8:00PM' },
  { value: 20.15, label: '8:15PM' },
  { value: 20.3, label: '8:30PM' },
  { value: 20.45, label: '8:45PM' },
  { value: 21, label: '9:00PM' },
  { value: 21.15, label: '9:15PM' },
  { value: 21.3, label: '9:30PM' },
  { value: 21.45, label: '9:45PM' },
  { value: 22, label: '10:00PM' },
];

export const discountTypeText = {
  fixedAmount: 'Fixed Amount',
  percentage: 'Percentage',
  removePlusPricing: 'Remove Plus Pricing',
} as const satisfies Record<Pegasus.ShippingDiscountType, string>;

export const usHolidayOptionText = {
  NEW_YEARS_DAY: "New Year's Day",
  CHRISTMAS_DAY: 'Christmas',
  INDEPENDENCE_DAY: 'Independence Day',
  LABOR_DAY: 'Labor Day',
  MARTIN_LUTHER_KING_JR_DAY: 'Martin Luther King Jr. Day',
  MEMORIAL_DAY: 'Memorial Day',
  PRESIDENTS_DAY: "Presidents' Day",
  THANKSGIVING_DAY: 'Thanksgiving',
  VETERANS_DAY: 'Veterans Day',
  INDIGENOUS_PEOPLES_DAY: 'Indigenous Peoples Day',
  JUNETEENTH: 'Juneteenth',
} as const satisfies Record<Pegasus.USHoliday, string>;

export const usHolidayOptions = Object.values(Pegasus.USHoliday).map(
  (holiday) => ({
    label: usHolidayOptionText[holiday],
    value: holiday,
  }),
);

export const advancedExchangeSortCriteriaLabel = {
  ALPHABETICAL: 'Alphabetical (A-Z)',
  NEWEST_FIRST: 'Newest First',
} satisfies Record<Pegasus.AdvancedExchangeSortCriteria, string>;

export const advancedExchangeLabel = {
  NO_PAYMENT_OR_REFUND: 'No Price Adjustments',
  PAYMENT: 'Upsell',
  PAYMENT_AND_PARTIAL_REFUND: 'Upsell & Downsell',
} satisfies Record<Pegasus.AdvancedExchangeMethod, string>;

export const advancedExchangeKindLabel = {
  product: 'Other Products',
  variant: 'Variants of the Same Product',
} satisfies Record<
  Pegasus.EventOfferAdvancedExchange['params']['exchangeKind'],
  string
>;

// Derived from Pegasus.OrderEditDisplayContent so all order-edit names share
// a single source of truth across both UIs.
export const orderEditActionLabel = Object.fromEntries(
  Object.entries(Pegasus.OrderEditDisplayContent).map(([editType, content]) => [
    editType,
    content.name,
  ]),
) as Record<Pegasus.OrderEditType, string>;

export const restockProviderActionLabel = {
  [Pegasus.RestockPlatform.SHOPIFY]: 'Shopify',
  [Pegasus.RestockPlatform.XORO]: 'Xoro',
} as const satisfies Record<Pegasus.RestockPlatform, string>;

export const shopifyRestockNameLabel = {
  [Pegasus.RestockName.available]: {
    label: 'Available',
    details: 'Items that can be sold again.',
  },
  [Pegasus.RestockName.damaged]: {
    label: 'Damaged',
    details:
      "Items that can't be sold again, displayed as unavailable in the Shopify Admin.",
  },

  [Pegasus.RestockName.none]: {
    label: 'None',
    details: 'Items will not be restocked.',
  },
} as const satisfies Record<
  Pegasus.RestockName,
  { label: string; details: string }
>;

export const shopNowBenefits: Pegasus.ShopNowBenefit[] = [
  {
    kind: 'incentive' as const,
    incentive: {
      amount: 10,
      type: CrewIncentiveTypeEnum.fixed,
    },
    icon: 'DollarSign' as const,
    label: 'Make more for your return',
    description:
      'Because we think we you can find something new to love, we have added a little extra to your return credit. ',
  },
  {
    kind: 'informational' as const,
    icon: 'MagnifyingGlass' as const,
    label: 'Explore our latest arrivals',
    description:
      'Browse the latest arrivals chosen to complement what you already love.',
  },
  {
    kind: 'informational' as const,
    icon: 'Truck' as const,
    label: 'Faster returns',
    description: 'Avoid out of stock delays, and enjoy your new items faster.',
  },
] as const;
